/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from 'componentregistry';
import { IORef } from 'designer/useIORef';
import useMounted from 'hooks/useMounted';
import React, { memo, ReactPortal, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export const IO_OVERLAY = 'io-overlay';

const canUseDOM = !!(
  typeof window !== 'undefined' &&
  window.document &&
  window.document.createElement
);

type Props = {
  parentRef: IORef<HTMLElement>;
  children: React.ReactNode;
  prepend?: boolean;
  itemId: string;
  overlayZIndex?: number;
  itemType: ItemType;
};

function OverlayPortal(props: Props): ReactPortal | null {
  const { parentRef, children, prepend = false, itemId, overlayZIndex } = props;
  const [div, setDiv] = useState<HTMLSpanElement | undefined>(undefined);
  const [ele, setEl] = useState<HTMLOrSVGElement | null>(null);
  const mounted = useMounted();
  useEffect(() => {
    return parentRef.subscribe((_el) => {
      setEl(_el);
    });
  }, [parentRef]);
  useEffect(() => {
    const el = ele as HTMLElement;
    if (el) {
      if (el.tagName === 'IMG') {
        return;
      }
      // CardContent uses this className='io-overlay' to ignore these overlay elements
      const _div = document.createElement('span');
      _div.className = IO_OVERLAY;
      if (overlayZIndex) {
        _div.style.zIndex = `${overlayZIndex}`;
      }
      setTimeout(() => {
        if (mounted.current && _div.className !== 'x') {
          setDiv(_div);
          if (prepend) {
            el.prepend(_div);
          } else {
            el.appendChild(_div);
          }
        } else {
          try {
            _div.remove();
          } catch (e) {
            // ignore
          }
        }
      }, 500);
      return () => {
        try {
          if (el.contains(_div)) {
            el.removeChild(_div);
          } else {
            // console.warn(
            //   'Missing overlay div and hence not removing from DOM!',
            // );
          }
          _div.className = 'x';
        } catch (e) {
          console.error(e);
        }
      };
    }
    // adding children to dependency so comps like EmbedPage
    // chidren get added after the overlay is added causing the overlay to render
    // below the children. by adding the children as dependency
    // the overlay get re-added on top of the children
  }, [ele, parentRef, prepend, children, itemId, mounted, overlayZIndex]);

  if (!canUseDOM) {
    return null;
  }
  return div ? createPortal(children, div) : null;
}

export default memo(OverlayPortal);
